import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Store, CheckCircle2, Ribbon, Palmtree, Heart, ShoppingBag, X, Truck, CreditCard } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, Empty, money } from "@/components/common";
import { Stars } from "@/pages/Purchases";
import { SellerReviews } from "@/components/SellerReviews";
import { ShowCard } from "@/components/ShowCard";
import AddressPicker from "@/components/AddressPicker";

export default function PublicStore() {
  const { sellerId } = useParams();
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [rev, setRev] = useState(null);
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checkoutProduct, setCheckoutProduct] = useState(null);
  const [tab, setTab] = useState("products");

  const loadReviews = () => api.get(`/sellers/${sellerId}/reviews`).then(setRev).catch(() => setRev({ average: 0, count: 0, reviews: [] }));
  useEffect(() => {
    api.get(`/sellers/${sellerId}`).then(setData).catch(() => setData(false));
    loadReviews();
  }, [sellerId]);
  useEffect(() => { setFollowing((user?.following || []).includes(sellerId)); }, [user, sellerId]);

  // Default to the tab that has content (Products first, else Live Shows).
  useEffect(() => {
    if (!data || data === false) return;
    const hasProducts = (data.products || []).length > 0;
    const hasLive = (data.shows || []).some((sh) => sh.status === "live" || sh.status === "scheduled");
    setTab(hasProducts ? "products" : hasLive ? "shows" : "products");
  }, [data]);

  const toggleFollow = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/follow/${sellerId}`);
      setFollowing(r.following);
      toast.success(r.following ? "You'll be notified when they go live" : "Unfollowed");
      refresh && refresh();
    } catch (err) { toast.error(err.message || "Could not update"); }
    finally { setBusy(false); }
  };

  if (data === null) return <Loading />;
  if (data === false) return <Empty icon={Store} title="Store not found" sub="This seller doesn't exist or is unavailable." />;

  const s = data.seller;
  const isSelf = user?.user_id === sellerId;
  const products = data.products || [];
  const liveShows = (data.shows || []).filter((sh) => sh.status === "live" || sh.status === "scheduled");
  const activeTab = tab;

  return (
    <div className="fade-up" style={{ maxWidth: 980, margin: "0 auto" }}>
      <div className="ss-card" style={{ padding: 24, marginBottom: 18, display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }} data-testid="public-store-header">
        <img src={s.avatar} alt="" style={{ width: 76, height: 76, borderRadius: 999, objectFit: "cover", border: "3px solid var(--gold)" }} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontWeight: 800, fontSize: 22, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {s.name}
            {s.seller_verified && <CheckCircle2 size={18} className="badge-verified" />}
            {s.founding_seller && <span className="badge-founding"><Ribbon size={11} /> Founding{s.founding_number ? ` #${s.founding_number}` : ""}</span>}
            {s.on_vacation && <span className="ss-pill" style={{ background: "var(--gold)", color: "var(--brand-2)", display: "inline-flex", alignItems: "center", gap: 4 }} data-testid="store-vacation-badge"><Palmtree size={12} /> On vacation</span>}
          </div>
          {s.username && <div style={{ marginTop: 3, fontWeight: 700, color: "var(--brand)" }} data-testid="public-store-handle">@{s.username}</div>}
          {s.seller_review_count > 0 && (
            <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }} data-testid="store-rating-badge">
              <Stars value={s.seller_rating} size={16} />
              <span style={{ fontWeight: 800 }}>{s.seller_rating.toFixed(1)}</span>
              <span className="ss-muted" style={{ fontSize: 13 }}>· {s.seller_review_count} review{s.seller_review_count === 1 ? "" : "s"}</span>
            </div>
          )}
          {s.bio && <div className="ss-muted" style={{ fontSize: 14, marginTop: 8 }}>{s.bio}</div>}
        </div>
        {!isSelf && (
          <button
            className={`ss-btn ss-btn-sm ${following ? "ss-btn-ghost" : "ss-btn-primary"}`}
            onClick={toggleFollow}
            disabled={busy}
            data-testid="store-follow-btn"
            style={{ alignSelf: "flex-start" }}
          >
            <Heart size={15} fill={following ? "currentColor" : "none"} /> {following ? "Following" : "Follow"}
          </button>
        )}
      </div>

      {s.about_store && (
        <div className="ss-card" style={{ padding: 22, marginBottom: 18 }}>
          <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>About the store</h3>
          <div style={{ fontSize: 15, whiteSpace: "pre-wrap" }}>{s.about_store}</div>
        </div>
      )}

      <div className="store-tabs" role="tablist" style={{ display: "flex", gap: 8, borderBottom: "1.5px solid var(--line)", marginBottom: 20 }}>
        {[
          { key: "products", label: "Products", count: products.length },
          { key: "shows", label: "Live Shows", count: liveShows.length },
        ].map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={activeTab === t.key}
            onClick={() => setTab(t.key)}
            data-testid={`store-tab-${t.key}`}
            style={{
              background: "none", border: "none", cursor: "pointer", padding: "10px 4px",
              marginRight: 18, fontSize: 15, fontWeight: 800,
              color: activeTab === t.key ? "var(--brand)" : "var(--muted)",
              borderBottom: `3px solid ${activeTab === t.key ? "var(--brand)" : "transparent"}`,
              marginBottom: -1.5, transition: "color .2s",
            }}
          >
            {t.label}
            <span
              className="ss-pill"
              style={{
                marginLeft: 8, fontSize: 11, padding: "1px 8px",
                background: activeTab === t.key ? "var(--gold-soft)" : "var(--surface-3, #eee)",
                color: activeTab === t.key ? "var(--brand)" : "var(--muted)",
              }}
            >{t.count}</span>
          </button>
        ))}
      </div>

      {activeTab === "products" && (
        <div style={{ marginBottom: 24 }} data-testid="store-products-tab">
          <p className="ss-muted" style={{ fontSize: 14, marginTop: -6, marginBottom: 16 }}>Buy directly from {s.name} — ships to you.</p>
          {products.length === 0 ? (
            <Empty icon={ShoppingBag} title="No products listed" sub="This seller hasn't added any buy-now items yet." />
          ) : (
            <div className="ss-grid grid-3" data-testid="store-products-grid">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} disabled={isSelf || s.on_vacation} onBuy={() => setCheckoutProduct(p)} />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "shows" && (
        <div style={{ marginBottom: 24 }} data-testid="store-shows-tab">
          <p className="ss-muted" style={{ fontSize: 14, marginTop: -6, marginBottom: 16 }}>Live and upcoming shows from {s.name}.</p>
          {liveShows.length === 0 ? (
            <Empty icon={Store} title="No live or upcoming shows" sub="This seller has nothing scheduled right now — follow them to get notified." />
          ) : (
            <div className="ss-grid grid-3" data-testid="store-shows-grid">
              {liveShows.map((sh) => <ShowCard key={sh.id} show={sh} />)}
            </div>
          )}
        </div>
      )}

      {rev && rev.reviews.length > 0 && (
        <div className="ss-card" style={{ padding: 22, marginBottom: 18 }}>
          <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 14 }}>What buyers are saying</h3>
          <SellerReviews reviews={rev.reviews} canReply={isSelf} onChange={loadReviews} />
        </div>
      )}

      {checkoutProduct && (
        <ProductCheckoutModal product={checkoutProduct} onClose={() => setCheckoutProduct(null)} />
      )}
    </div>
  );
}

function ProductCard({ product, disabled, onBuy }) {
  const soldOut = (product.qty || 0) <= 0;
  return (
    <div className="ss-card" style={{ overflow: "hidden", display: "flex", flexDirection: "column" }} data-testid={`store-product-${product.id}`}>
      <div style={{ position: "relative", height: 220, flexShrink: 0, background: "var(--line)" }}>
        {product.image
          ? <img src={product.image} alt={product.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", color: "var(--muted)" }}><ShoppingBag size={40} /></div>}
      </div>
      <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
        <div style={{ fontWeight: 800, fontSize: 15, lineHeight: 1.3 }}>{product.title}</div>
        {product.description && <div className="ss-muted" style={{ fontSize: 13, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{product.description}</div>}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "auto", gap: 8 }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>{money(product.price)}</div>
          {product.qty > 0 && product.qty <= 5 && <span className="ss-muted" style={{ fontSize: 12 }}>Only {product.qty} left</span>}
        </div>
        <button
          className="ss-btn ss-btn-primary ss-btn-block"
          disabled={disabled || soldOut}
          onClick={onBuy}
          data-testid={`store-buy-${product.id}`}
        >
          <ShoppingBag size={16} /> {soldOut ? "Sold out" : "Buy now"}
        </button>
      </div>
    </div>
  );
}

function ProductCheckoutModal({ product, onClose }) {
  const price = product.price || 0;
  const [step, setStep] = useState("address");
  const [addr, setAddr] = useState({ name: "", phone: "", street1: "", street2: "", city: "", state: "", zip: "", country: "US" });
  const [rates, setRates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [requiresShipping, setRequiresShipping] = useState(true);
  const [warnings, setWarnings] = useState([]);
  const [busy, setBusy] = useState(false);

  const pay = async (rate) => {
    setBusy(true);
    try {
      const res = await api.post("/payments/checkout-product", {
        product_id: product.id, origin_url: window.location.origin,
        ship_to: requiresShipping ? addr : null, shipping_rate: rate || null,
      });
      window.location.href = res.checkout_url;
    } catch (err) { toast.error(err.message || "Couldn't start checkout"); setBusy(false); }
  };

  const getRates = async (e) => {
    e.preventDefault();
    setBusy(true); setWarnings([]);
    try {
      const v = await api.post("/shipping/validate-address", { address: addr });
      if (!v.valid) { setWarnings(v.messages); setBusy(false); return; }
      const d = await api.post("/shipping/quote-product", { product_id: product.id, ship_to: addr });
      if (d.requires_shipping === false) { setRequiresShipping(false); await pay(null); return; }
      setRates(d.rates); setSelected(d.rates[0]?.rate_id); setStep("rate"); setBusy(false);
    } catch (err) { toast.error(err.message); setBusy(false); }
  };

  const rate = rates.find((r) => r.rate_id === selected);
  const total = price + (rate?.buyer_charge || 0);

  return (
    <div className="ss-modal-back" onClick={onClose}>
      <div className="ss-modal" onClick={(e) => e.stopPropagation()} data-testid="product-checkout-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h2 style={{ fontSize: 20 }}>Checkout</h2>
          <button className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose} data-testid="product-checkout-close"><X size={18} /></button>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14 }}>
          {product.image && <img src={product.image} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover" }} />}
          <div style={{ flex: 1 }}><div style={{ fontWeight: 800 }}>{product.title}</div><div className="ss-muted" style={{ fontSize: 13 }}>Item {money(price)}</div></div>
        </div>

        {step === "address" && (
          <form onSubmit={getRates} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <AddressPicker onChange={setAddr} warnings={warnings} testPrefix="pco" initial={addr} />
            <button className="ss-btn ss-btn-primary ss-btn-block" disabled={busy} data-testid="pco-get-rates"><Truck size={16} /> {busy ? "Getting rates…" : "Get shipping rates"}</button>
          </form>
        )}

        {step === "rate" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontWeight: 800, fontSize: 14 }}>Choose shipping</div>
            {rates.map((r) => (
              <label key={r.rate_id} className="ss-card" style={{ padding: 12, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", borderColor: selected === r.rate_id ? "var(--brand)" : "var(--line)" }} data-testid={r.flat ? "pco-rate-flat" : `pco-rate-${r.carrier}`.replace(/\s+/g, "-")}>
                <input type="radio" name="pcorate" checked={selected === r.rate_id} onChange={() => setSelected(r.rate_id)} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                    {r.flat ? (r.international ? "International flat rate" : "Flat rate shipping") : `${r.carrier} · ${r.service}`}
                    {r.flat && <span className="ss-pill" style={{ background: "var(--gold)", color: "var(--brand-2)", fontSize: 11 }}>{r.international ? "Intl" : "Set price"}</span>}
                  </div>
                  <div className="ss-muted" style={{ fontSize: 12 }}>{r.flat ? (r.international ? "Flat international rate set by the seller" : "One flat rate set by the seller") : r.delivery_window}</div>
                </div>
                <div style={{ fontWeight: 800 }}>{money(r.buyer_charge)}</div>
              </label>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: 16, marginTop: 4 }}>
              <span>Total</span><span data-testid="pco-total">{money(total)}</span>
            </div>
            <button className="ss-btn ss-btn-primary ss-btn-block" disabled={busy || !rate} onClick={() => pay(rate)} data-testid="pco-pay"><CreditCard size={16} /> {busy ? "Redirecting…" : `Pay ${money(total)}`}</button>
            <button type="button" className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => setStep("address")} data-testid="pco-back">Edit address</button>
          </div>
        )}
      </div>
    </div>
  );
}
